/**
 * Full-screen glass deployment overlay with animated phases, stats, and gold accents.
 */
import {
  ArrowLeft,
  ArrowLeftRight,
  Bot,
  Box,
  Coins,
  Droplets,
  Fingerprint,
  GitBranch,
  Hexagon,
  Layers,
  Route,
  Search,
  Wallet,
  Zap,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";

import { DEPLOY_PHASES, FORK_PHASES } from "../deploy/progress.js";

import type { DeploymentProgress, PhaseInfo } from "../deploy/progress.js";
import type { LucideIcon } from "lucide-react";

const VaultLockMini = lazy(() => import("./VaultLockMini.js"));

/** Map icon name strings to lucide components. */
const ICON_MAP: Record<string, LucideIcon> = {
  Box,
  GitBranch,
  Layers,
  Hexagon,
  Route,
  Zap,
  Fingerprint,
  Coins,
  Droplets,
  ArrowLeftRight,
  Bot,
  Search,
  Wallet,
};

interface DeploymentOverlayProps {
  progress: DeploymentProgress;
  /** Smooth percentage 0-100. */
  smoothPercent: number;
  /** Live elapsed milliseconds from hook timer. */
  liveElapsedMs: number;
  /** Called when user clicks Continue after completion. */
  onContinue?: () => void;
  /** Called when user clicks Try Again after error. */
  onRetry?: () => void;
  /** Called when user clicks Back during deployment to return to mode selection. */
  onCancel?: () => void;
}

function formatGas(gas: bigint): string {
  if (gas === 0n) return "0";
  const millions = Number(gas) / 1_000_000;
  if (millions >= 1) return `${millions.toFixed(1)}M`;
  const thousands = Number(gas) / 1_000;
  return `${thousands.toFixed(0)}K`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${String(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Animated SVG checkmark. */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M3 8.5 L6.5 12 L13 4"
        style={{
          strokeDasharray: 20,
          strokeDashoffset: 0,
          animation: "check-draw 0.3s ease-out forwards",
        }}
      />
    </svg>
  );
}

/** Gold spinning ring for active phase. */
function SpinnerRing() {
  return (
    <svg
      className="animate-spin"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      style={{ animationDuration: "2s" }}
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="var(--color-gold-dim)"
        strokeWidth="1.5"
        opacity="0.3"
      />
      <path
        d="M8 2 A6 6 0 0 1 14 8"
        stroke="var(--color-gold)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Single phase row in the list. */
function PhaseRow({
  phase,
  status,
  detail,
  index,
}: {
  phase: PhaseInfo;
  status: "done" | "active" | "pending";
  detail?: { durationMs?: number; contractsDeployed: number };
  index: number;
}) {
  const Icon = ICON_MAP[phase.icon];

  return (
    <div
      className={`phase-row flex items-center gap-3 py-1.5${status === "active" ? "phase-active-accent" : ""}`}
      data-status={status}
      style={{
        opacity: status === "pending" ? 0.35 : 1,
        animation:
          status !== "pending"
            ? `overlay-fadeUp 0.3s ease-out ${String(index * 50)}ms both`
            : undefined,
        paddingLeft: status === "active" ? undefined : 4,
      }}
    >
      {/* Status indicator */}
      <div className="phase-status-icon flex w-4 items-center justify-center">
        {status === "done" && (
          <CheckIcon className="phase-check text-[var(--color-success)]" />
        )}
        {status === "active" && <SpinnerRing />}
        {status === "pending" && (
          <span
            className="block h-1 w-1 rounded-full"
            style={{ background: "var(--color-text-muted)" }}
          />
        )}
      </div>

      {/* Icon */}
      <div
        className="phase-icon-wrap flex w-5 items-center justify-center"
        style={{
          color:
            status === "done"
              ? "var(--color-success)"
              : status === "active"
                ? "var(--color-gold)"
                : "var(--color-text-muted)",
        }}
      >
        {Icon && <Icon size={14} />}
      </div>

      {/* Name */}
      <span
        className={`flex-1 font-mono text-xs${status === "active" ? "phase-name-shimmer" : ""}`}
        style={{
          color:
            status === "done"
              ? "var(--color-text-dim)"
              : status === "active"
                ? "var(--color-gold)"
                : "var(--color-text-muted)",
        }}
      >
        {phase.name}
      </span>

      {/* Stats for completed phases */}
      {status === "done" && detail && (
        <span
          className="font-mono text-[10px]"
          style={{ color: "var(--color-text-muted)" }}
        >
          {detail.durationMs !== undefined && formatDuration(detail.durationMs)}
          {detail.contractsDeployed > 0 && (
            <span className="ml-2">{String(detail.contractsDeployed)} tx</span>
          )}
        </span>
      )}

      {/* Animated dots for active phase */}
      {status === "active" && (
        <span
          className="font-mono text-[10px]"
          style={{ color: "var(--color-gold-dim)" }}
        >
          <AnimatedDots />
        </span>
      )}
    </div>
  );
}

/** Three animated dots for "loading" state. */
function AnimatedDots() {
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const id = setInterval(() => {
      setDots((d) => (d % 3) + 1);
    }, 400);
    return () => {
      clearInterval(id);
    };
  }, []);
  return <>{".".repeat(dots)}</>;
}

export function DeploymentOverlay({
  progress,
  smoothPercent,
  liveElapsedMs,
  onContinue,
  onRetry,
  onCancel,
}: DeploymentOverlayProps) {
  const isFork = progress.totalPhases <= 3;
  const phases = isFork ? FORK_PHASES : DEPLOY_PHASES;
  const isComplete = progress.phase === "complete";
  const isError = progress.phase === "error";
  const [showSummary, setShowSummary] = useState(false);

  // Auto-show summary after completion
  useEffect(() => {
    if (isComplete) {
      const id = setTimeout(() => {
        setShowSummary(true);
      }, 600);
      return () => {
        clearTimeout(id);
      };
    }
    setShowSummary(false);
  }, [isComplete]);

  // Auto-continue after 2s
  useEffect(() => {
    if (isComplete && onContinue) {
      const id = setTimeout(onContinue, 2500);
      return () => {
        clearTimeout(id);
      };
    }
  }, [isComplete, onContinue]);

  const pct = isComplete ? 100 : smoothPercent;
  const elapsedSec = Math.round(
    (isComplete ? progress.elapsedMs : liveElapsedMs) / 1000,
  );

  // Progress-reactive card styling — borders handled by CSS ::before/::after pseudo-elements
  const t = pct / 100; // 0-1 normalized
  const cardStyle = useMemo(() => {
    if (isError) {
      return {
        filter: "drop-shadow(5px 5px 0 rgba(0, 0, 0, 0.40))",
      };
    }
    return {};
  }, [t, isError]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Glass backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(10, 10, 10, 0.75)",
          backdropFilter: "blur(8px)",
        }}
      />

      <div className="relative z-10 w-full max-w-xl px-4">
        {/* Gold progress bar */}
        <div
          className="mb-5 h-[3px] w-full overflow-hidden"
          style={{ background: "rgba(201, 168, 76, 0.08)" }}
        >
          <div
            className={[
              "deploy-progress-fill h-full",
              !isComplete && !isError ? "deploy-progress-active" : "",
            ].join(" ")}
            style={{
              width: `${String(pct)}%`,
              background: isError
                ? "var(--color-error)"
                : `linear-gradient(90deg, rgba(201, 168, 76, ${(0.4 + t * 0.6).toFixed(2)}), rgba(255, 236, 171, ${(0.6 + t * 0.4).toFixed(2)}))`,
              filter: isError
                ? "drop-shadow(0 0 6px rgba(239, 68, 68, 0.4))"
                : `drop-shadow(0 0 ${String(Math.round(4 + t * 10))}px rgba(201, 168, 76, ${(0.2 + t * 0.4).toFixed(2)}))`,
              transition:
                "width 800ms cubic-bezier(0.22, 1, 0.36, 1), background 800ms ease, filter 800ms ease",
            }}
          />
        </div>

        {/* Vault lock animation */}
        <Suspense fallback={null}>
          <VaultLockMini
            progress={pct / 100}
            phaseIndex={Math.min(progress.phaseIndex, progress.totalPhases)}
            totalPhases={progress.totalPhases}
            isComplete={isComplete}
            isError={isError}
            className="mb-4"
          />
        </Suspense>

        {/* Breathing glow wrapper — drop-shadow pulse follows octagonal shape */}
        <div
          className={
            [
              !isComplete && !isError ? "deploy-glow-active" : "",
              isComplete ? "deploy-glow-complete" : "",
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
        >
          {/* System message container — hard-edge shadow, gold top-line, aggressive octagonal cut */}
          <div
            className="deploy-card deploy-system-card octa-xl px-4 py-5 sm:px-8 sm:py-7"
            style={cardStyle}
          >
            {/* Header — NieR system message style */}
            <div className="mb-5 flex items-baseline justify-between">
              <div className="flex items-center gap-3">
                <span
                  className="inline-block h-[6px] w-[6px]"
                  style={{ background: "rgba(201, 168, 76, 0.4)" }}
                />
                <h2
                  className="font-serif text-lg"
                  style={{ color: "var(--color-gold)" }}
                >
                  Deploying Environment
                </h2>
              </div>
              <span
                className="font-mono text-[10px] uppercase"
                style={{
                  color: "var(--color-text-muted)",
                  letterSpacing: "0.12em",
                }}
              >
                Phase{" "}
                {String(
                  Math.min(progress.phaseIndex + 1, progress.totalPhases),
                )}
                /{String(progress.totalPhases)}
              </span>
            </div>

            {/* Phase list */}
            <div className="mb-5">
              {phases.map((phase, i) => {
                const done = progress.phaseIndex > phase.index || isComplete;
                const active =
                  progress.phaseIndex === phase.index &&
                  !isComplete &&
                  !isError;
                const status: "done" | "active" | "pending" = done
                  ? "done"
                  : active
                    ? "active"
                    : "pending";

                const detail = progress.phaseDetails[phase.id];

                return (
                  <div key={phase.id}>
                    <PhaseRow
                      phase={phase}
                      status={status}
                      detail={detail}
                      index={i}
                    />
                    {status === "active" && progress.currentAction && (
                      <div
                        className="ml-9 truncate font-mono text-[10px] sm:ml-[52px]"
                        style={{ color: "var(--color-text-muted)" }}
                      >
                        {progress.currentAction}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Stats footer — octagonal stat cells */}
            <div className="grid grid-cols-3 gap-1.5 text-center sm:gap-3">
              {[
                {
                  value: formatGas(progress.totalGasUsed),
                  label: "Gas",
                  delay: 0,
                },
                {
                  value: `${String(progress.contractsDeployed)}/${String(progress.totalContracts)}`,
                  label: "Contracts",
                  delay: 60,
                },
                {
                  value: String(elapsedSec),
                  suffix: "s",
                  label: "Elapsed",
                  delay: 120,
                  serif: true,
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="deploy-stat-cell octa-md py-2.5"
                  style={{
                    animation: `overlay-fadeUp 0.3s ease-out ${String(stat.delay)}ms both`,
                  }}
                >
                  <div
                    className={`deploy-stat-value text-sm ${stat.serif ? "font-serif" : "font-mono"}`}
                    style={{ color: "var(--color-text)" }}
                  >
                    {stat.value}
                    {stat.suffix && (
                      <span
                        className="ml-0.5 font-mono text-[10px]"
                        style={{ color: "var(--color-text-muted)" }}
                      >
                        {stat.suffix}
                      </span>
                    )}
                  </div>
                  <div
                    className="mt-0.5 font-mono text-[10px] uppercase"
                    style={{
                      color: "var(--color-text-muted)",
                      letterSpacing: "var(--tracking-mono)",
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Cancel / back button — visible during active deployment */}
            {!isComplete && !isError && onCancel && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={onCancel}
                  className="flex items-center gap-1.5 font-mono text-[11px] transition-all duration-300"
                  style={{ color: "var(--color-text-muted)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--color-text-dim)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--color-text-muted)";
                  }}
                >
                  <ArrowLeft size={12} />
                  Back
                </button>
              </div>
            )}

            {/* Completion summary */}
            {isComplete && showSummary && (
              <div
                className="mt-5 text-center"
                style={{ animation: "overlay-fadeUp 0.4s ease-out" }}
              >
                <p
                  className="font-serif text-sm"
                  style={{ color: "var(--color-gold)" }}
                >
                  Environment ready
                </p>
                {onContinue && (
                  <button
                    onClick={onContinue}
                    className="octa-md mt-3 px-6 py-2 font-sans text-xs font-medium transition-all duration-300"
                    style={{
                      color: "var(--color-gold)",
                      background: "rgba(201, 168, 76, 0.08)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(201, 168, 76, 0.15)";
                      e.currentTarget.style.filter =
                        "drop-shadow(0 0 8px rgba(201, 168, 76, 0.2))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(201, 168, 76, 0.08)";
                      e.currentTarget.style.filter = "none";
                    }}
                  >
                    Continue
                  </button>
                )}
              </div>
            )}

            {/* Error state */}
            {isError && (
              <div
                className="octa-md mt-5 p-3"
                style={{ background: "rgba(239, 68, 68, 0.08)" }}
              >
                <p className="text-xs" style={{ color: "var(--color-error)" }}>
                  {progress.error}
                </p>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="octa-sm mt-2 px-4 py-1.5 font-sans text-xs transition-all duration-300"
                    style={{
                      color: "var(--color-error)",
                      background: "rgba(239, 68, 68, 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(239, 68, 68, 0.18)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(239, 68, 68, 0.1)";
                    }}
                  >
                    Try Again
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Keyframes: check-draw, fadeUp, phase micro-effects, card breathing */}
      {/* prettier-ignore */}
      <style>{`
        @keyframes check-draw {
          from { stroke-dashoffset: 20; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes overlay-fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* --- Deploy card breathing + shimmer --- */

        /* Slow, subtle border glow pulse on wrapper (not the card bg) */
        @keyframes deploy-glow-pulse {
          0%, 100% {
            filter:
              drop-shadow(0 0 1px rgba(201, 168, 76, 0.15))
              drop-shadow(0 0 4px rgba(201, 168, 76, 0.04))
              drop-shadow(0 4px 20px rgba(0, 0, 0, 0.25));
          }
          50% {
            filter:
              drop-shadow(0 0 1.5px rgba(201, 168, 76, 0.35))
              drop-shadow(0 0 12px rgba(201, 168, 76, 0.08))
              drop-shadow(0 4px 20px rgba(0, 0, 0, 0.25));
          }
        }
        .deploy-card {
          position: relative;
        }
        .deploy-glow-active {
          animation: deploy-glow-pulse 5s ease-in-out infinite;
        }

        /* Progress bar shimmer sweep */
        @keyframes deploy-shimmer-sweep {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }

        /* Progress bar: traveling highlight within fill */
        .deploy-progress-fill {
          position: relative;
          overflow: hidden;
        }
        .deploy-progress-active::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 40%,
            rgba(255, 236, 171, 0.5) 50%,
            rgba(255, 255, 255, 0.3) 60%,
            transparent 100%
          );
          animation: deploy-shimmer-sweep 2s cubic-bezier(0.37, 0, 0.63, 1) infinite;
        }

        /* Completion bloom — golden flare transitioning to success green */
        @keyframes deploy-complete-bloom {
          0% {
            filter:
              drop-shadow(0 0 2px rgba(201, 168, 76, 0.4))
              drop-shadow(0 0 20px rgba(201, 168, 76, 0.15));
          }
          40% {
            filter:
              drop-shadow(0 0 2px rgba(52, 209, 106, 0.35))
              drop-shadow(0 0 16px rgba(52, 209, 106, 0.1));
          }
          100% {
            filter:
              drop-shadow(0 0 1px rgba(52, 209, 106, 0.2))
              drop-shadow(0 0 8px rgba(52, 209, 106, 0.05));
          }
        }
        .deploy-glow-complete {
          animation: deploy-complete-bloom 3s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        /* Stat value count-up feel */
        @keyframes deploy-stat-pop {
          0% { transform: scale(0.9); opacity: 0.5; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        .deploy-stat-value {
          animation: deploy-stat-pop 0.3s ease-out;
        }

        /* --- Phase row micro-effects --- */

        /* Active phase: gold glow pulse on icon wrapper */
        @keyframes phase-glow-pulse {
          0%, 100% { box-shadow: 0 0 8px 2px rgba(201,168,76,0.15); }
          50% { box-shadow: 0 0 14px 4px rgba(201,168,76,0.3); }
        }
        .phase-row[data-status="active"] .phase-icon-wrap {
          animation: phase-glow-pulse 2s ease-in-out infinite;
          border-radius: 50%;
        }

        /* Active phase: gold left border pulse */
        @keyframes phase-border-pulse {
          0%, 100% { border-left-color: rgba(201,168,76,0.2); }
          50% { border-left-color: rgba(201,168,76,0.5); }
        }
        .phase-row[data-status="active"] {
          animation: phase-border-pulse 2s ease-in-out infinite, overlay-fadeUp 0.3s ease-out both;
        }

        /* Active phase name: gold shimmer */
        @keyframes phase-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .phase-name-shimmer {
          background: linear-gradient(
            90deg,
            var(--color-gold) 0%,
            rgba(255,236,171,0.9) 50%,
            var(--color-gold) 100%
          );
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: phase-shimmer 3s linear infinite;
        }

        /* Completed phase: golden sparkle on checkmark */
        @keyframes phase-sparkle {
          0% { filter: brightness(1) drop-shadow(0 0 0 transparent); }
          30% { filter: brightness(2.5) drop-shadow(0 0 6px rgba(201,168,76,0.6)); }
          100% { filter: brightness(1) drop-shadow(0 0 0 transparent); }
        }
        .phase-row[data-status="done"] .phase-check {
          animation: phase-sparkle 0.8s ease-out;
        }

        /* Respect prefers-reduced-motion */
        @media (prefers-reduced-motion: reduce) {
          .deploy-glow-active,
          .deploy-glow-complete,
          .phase-row[data-status="active"] .phase-icon-wrap,
          .phase-row[data-status="active"],
          .phase-name-shimmer,
          .phase-row[data-status="done"] .phase-check {
            animation: none !important;
          }
          .phase-name-shimmer {
            background: none !important;
            -webkit-text-fill-color: var(--color-gold) !important;
          }
        }
      `}</style>
    </div>
  );
}

export default DeploymentOverlay;
